import { useState } from 'react';
import { addTestimonial } from '../../../data/testimonialsData';

interface TestimonialFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function TestimonialForm({ onSuccess, onCancel }: TestimonialFormProps) {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      setError('Veuillez remplir votre nom et votre avis.');
      return;
    }
    if (text.length > 500) {
      setError('Votre avis ne doit pas dépasser 500 caractères.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('role', role);
      formData.append('company', company);
      formData.append('rating', String(rating));
      formData.append('text', text);

      await fetch('https://readdy.ai/api/form/d6pvmbc4k19g20dvr1h0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      addTestimonial({ name, role, company, rating, text });
      onSuccess();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 text-left">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Laisser un avis</h3>
        <button
          onClick={onCancel}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-gray-500 text-xl"></i>
        </button>
      </div>

      <form data-readdy-form id="testimonial-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Votre note *</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="w-8 h-8 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
              >
                <i className={`text-2xl ${star <= (hoverRating || rating) ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'}`}></i>
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-500 self-center">
              {rating === 1 ? 'Mauvais' : rating === 2 ? 'Passable' : rating === 3 ? 'Bien' : rating === 4 ? 'Très bien' : 'Excellent'}
            </span>
          </div>
        </div>

        {/* Nom */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Votre nom *</label>
          <input
            type="text"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex : Jean Dupont"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            required
          />
        </div>

        {/* Rôle et entreprise */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Votre fonction</label>
            <input
              type="text"
              name="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Ex : Gérant"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise / Lieu</label>
            <input
              type="text"
              name="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Ex : Restaurant Le Soleil"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Avis */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Votre avis * <span className="text-gray-400 font-normal">({text.length}/500)</span>
          </label>
          <textarea
            name="text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 500))}
            placeholder="Décrivez votre expérience avec R3D Nuisibles..."
            rows={4}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
            required
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-error-warning-line"></i>
            </div>
            {error}
          </div>
        )}

        <p className="text-xs text-gray-400">
          Votre avis sera publié après validation par notre équipe.
        </p>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Envoi...' : 'Envoyer mon avis'}
          </button>
        </div>
      </form>
    </div>
  );
}
